/**
 * Phase 5E — Executive Command Centre (light-theme legible variant)
 *
 * Single screen surfacing v2 governance state:
 *  - AI budget month-to-date
 *  - Pipeline summary (cards by stage)
 *  - Workspace performance (latest outcome snapshots)
 *  - Active campaigns with progress vs targets
 *  - Open escalations grouped by severity
 *  - Recent audit activity
 *
 * Backed by: GET /api/v2/workspaces/{ws}/dashboard
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { fetchMyWorkspaces } from "@/lib/workspaceFetch";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertTriangle, Activity, DollarSign, TrendingUp, Target, Zap, Users, Sparkles, Loader2, Upload, FileSpreadsheet } from "lucide-react";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const STAGE_ORDER = [
  "BACKLOG", "READY_FOR_DESIGN", "IN_DESIGN", "IN_REVIEW",
  "PENDING_APPROVAL", "APPROVED", "REJECTED", "SCHEDULED",
  "PUBLISHED", "COMPLETED", "ARCHIVED",
];

const SEVERITY_COLORS = {
  info:     "bg-sky-100   text-sky-900   border-sky-300",
  warning:  "bg-amber-100 text-amber-900 border-amber-300",
  critical: "bg-red-100   text-red-900   border-red-300",
};

function fmtCurrency(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function fmtNum(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}

async function apiCall(path) {
  return fetch(`${API}${path}`, { credentials: "include" });
}

export default function ExecutiveCommandCentre({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const workspaces = await fetchMyWorkspaces();
      if (!Array.isArray(workspaces) || !workspaces.length) throw new Error("No v2 workspace membership for this account");
      const ws = workspaces[0];
      setWorkspaceId(ws.workspace_id);
      const r = await apiCall(`/v2/workspaces/${ws.workspace_id}/dashboard`);
      if (!r.ok) throw new Error(`Dashboard ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !data) {
    return (
      <div className="p-8 space-y-6" data-testid="ecc-loading">
        <Skeleton className="h-12 w-1/3" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[0,1,2,3].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8" data-testid="ecc-error">
        <Card className="bg-red-50 border-red-300">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Command Centre offline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 text-sm">{error}</p>
            <Button onClick={refresh} className="mt-4" data-testid="ecc-retry-btn">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const d = data;
  const aiPct = d.ai_budget.budget_usd > 0 ? Math.min(100, (d.ai_budget.spend_usd / d.ai_budget.budget_usd) * 100) : 0;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="executive-command-centre">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Executive Command Centre</p>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            {d.workspace.name}
          </h1>
          <p className="text-sm text-slate-600 mt-1">Period: {d.period} · Plan: {d.workspace.plan}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCsvOpen(true)}
            disabled={!workspaceId}
            data-testid="ecc-import-csv-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import LinkedIn CSV
          </Button>
          <Button variant="outline" onClick={refresh} data-testid="ecc-refresh-btn">Refresh</Button>
        </div>
      </div>

      <CsvImportDialog
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        workspaceId={workspaceId}
        onImported={refresh}
      />

      {/* Top row: 4 KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiTile
          icon={<DollarSign className="h-4 w-4" />}
          label="AI Spend MTD"
          value={fmtCurrency(d.ai_budget.spend_usd)}
          sub={`${fmtCurrency(d.ai_budget.remaining_usd)} of ${fmtCurrency(d.ai_budget.budget_usd)} left · ${d.ai_budget.calls} calls`}
          accent={aiPct > 80 ? "warn" : "ok"}
          progress={aiPct}
          testid="ecc-ai-budget-tile"
        />
        <KpiTile
          icon={<Zap className="h-4 w-4" />}
          label="Pipeline"
          value={fmtNum(d.pipeline.total_cards)}
          sub={`${d.pipeline.published_count} published · ${(d.pipeline.by_stage.IN_DESIGN || 0)} in design`}
          accent="ok"
          testid="ecc-pipeline-tile"
        />
        <KpiTile
          icon={<TrendingUp className="h-4 w-4" />}
          label="Performance"
          value={fmtNum(d.performance.impressions)}
          sub={`${fmtNum(d.performance.leads)} leads · ${fmtCurrency(d.performance.revenue_usd)} attributed`}
          accent="ok"
          testid="ecc-performance-tile"
        />
        <KpiTile
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Open Escalations"
          value={fmtNum(d.escalations.open_count)}
          sub={`${d.escalations.by_severity.critical || 0} critical · ${d.escalations.by_severity.warning || 0} warning`}
          accent={d.escalations.open_count > 0 ? "warn" : "ok"}
          testid="ecc-escalations-tile"
        />
      </div>

      {/* Pipeline by stage */}
      <Card className="bg-white border-slate-200 shadow-sm" data-testid="ecc-pipeline-card">
        <CardHeader>
          <CardTitle className="text-slate-900 text-base flex items-center gap-2">
            <Users className="h-4 w-4" /> Production pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {STAGE_ORDER.map((stage) => {
              const count = d.pipeline.by_stage[stage] || 0;
              if (count === 0) return null;
              return (
                <div
                  key={stage}
                  className="rounded-lg bg-slate-50 border border-slate-200 p-3"
                  data-testid={`ecc-stage-${stage.toLowerCase()}`}
                >
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{stage.replace(/_/g, " ")}</p>
                  <p className="text-2xl font-semibold text-slate-900 mt-1">{count}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Active campaigns + Top performers row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="ecc-campaigns-card">
          <CardHeader>
            <CardTitle className="text-slate-900 text-base flex items-center gap-2">
              <Target className="h-4 w-4" /> Active campaigns
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {d.active_campaigns.length === 0 ? (
              <p className="text-sm text-slate-500">No active campaigns. Create one to track Q-targets.</p>
            ) : d.active_campaigns.map((c) => (
              <div
                key={c.campaign_id}
                className="rounded-lg bg-slate-50 border border-slate-200 p-4"
                data-testid={`ecc-campaign-${c.campaign_id}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-slate-900 font-medium">{c.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.starts_on} → {c.ends_on || "open"} · {c.card_count} cards</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300">{c.status}</Badge>
                </div>
                {Object.keys(c.progress_vs_targets).length > 0 ? (
                  <div className="space-y-2 mt-3">
                    {Object.entries(c.progress_vs_targets).map(([key, p]) => (
                      <div key={key}>
                        <div className="flex justify-between text-xs text-slate-700 mb-1">
                          <span className="capitalize font-medium">{key.replace(/_/g, " ")}</span>
                          <span className="text-slate-600">{key === "revenue_usd" ? fmtCurrency(p.actual) : fmtNum(p.actual)} / {key === "revenue_usd" ? fmtCurrency(p.target) : fmtNum(p.target)}</span>
                        </div>
                        <Progress value={p.ratio * 100} className="h-1.5" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mt-2">No targets set on this campaign.</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm" data-testid="ecc-top-cards-card">
          <CardHeader>
            <CardTitle className="text-slate-900 text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Top performers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {d.top_cards.length === 0 ? (
              <p className="text-sm text-slate-500">Record outcome metrics on published cards to see rankings.</p>
            ) : d.top_cards.map((t) => (
              <div
                key={t.card_id}
                className="flex items-center justify-between rounded-md bg-slate-50 border border-slate-200 px-3 py-2"
                data-testid={`ecc-top-card-${t.card_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-900 truncate font-medium">{t.title || t.card_id}</p>
                  <p className="text-[11px] text-slate-600">{t.platform} · {((t.engagement_rate || 0) * 100).toFixed(1)}% eng · {t.leads} leads</p>
                </div>
                <Badge className="bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300 ml-2">
                  Q {((t.quality_signal || 0) * 100).toFixed(0)}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Rex Strategy Weekly widget */}
      <RexStrategyWidget />

      {/* Escalations + Audit activity row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="ecc-escalations-card">          <CardHeader>
            <CardTitle className="text-slate-900 text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Open escalations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {d.escalations.recent.length === 0 ? (
              <p className="text-sm text-slate-500">All clear.</p>
            ) : d.escalations.recent.map((e) => (
              <div
                key={e.id}
                className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2"
                data-testid={`ecc-escalation-${e.id}`}
              >
                <div className="flex justify-between items-start">
                  <p className="text-sm text-slate-900 font-medium">{e.detail?.title || e.entity_id}</p>
                  <Badge className={SEVERITY_COLORS[e.severity] || ""}>{e.severity}</Badge>
                </div>
                <p className="text-[11px] text-slate-600 mt-0.5">
                  {e.detail?.rule_name} · {e.detail?.stage} · stuck since {(e.detail?.stuck_since || "").slice(0, 16).replace("T", " ")}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm" data-testid="ecc-activity-card">
          <CardHeader>
            <CardTitle className="text-slate-900 text-base flex items-center gap-2">
              <Activity className="h-4 w-4" /> Recent activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {d.recent_activity.length === 0 ? (
              <p className="text-sm text-slate-500">No activity yet.</p>
            ) : d.recent_activity.map((a) => (
              <div
                key={a.id}
                className="flex justify-between items-center text-xs px-2 py-1.5 rounded hover:bg-slate-50"
                data-testid={`ecc-activity-${a.id}`}
              >
                <div className="min-w-0 flex-1">
                  <span className="text-slate-900 font-medium">{a.action}</span>
                  <span className="text-slate-500"> on {a.entity_type}</span>
                </div>
                <span className="text-slate-500 text-[10px] ml-2">{a.actor_email?.split("@")[0]}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiTile({ icon, label, value, sub, accent = "ok", progress, testid }) {
  const accentClass = accent === "warn"
    ? "border-amber-300 bg-amber-50"
    : "border-slate-200 bg-white";
  return (
    <Card className={`${accentClass} shadow-sm`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between text-slate-600 text-xs uppercase tracking-wide font-semibold">
          <span>{label}</span>
          <span className="text-slate-500">{icon}</span>
        </div>
        <p className="text-2xl lg:text-3xl font-semibold text-slate-900 mt-2">{value}</p>
        {sub && <p className="text-xs text-slate-600 mt-1">{sub}</p>}
        {progress != null && <Progress value={progress} className="h-1 mt-3" />}
      </CardContent>
    </Card>
  );
}


function RexStrategyWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
      const API = `${_API_BASE}/api`;
      const exec = async (action, parameters = {}) => {
        const r = await fetch(`${API}/ask-rex/agent/execute`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action, parameters }),
        });
        return r.ok ? r.json() : null;
      };
      const [cadence, mix, risks, benchmark] = await Promise.all([
        exec("RECOMMEND_CADENCE"),
        exec("RECOMMEND_TOPIC_MIX"),
        exec("FLAG_CONTENT_RISKS"),
        exec("BENCHMARK_VS_TARGET"),
      ]);
      setData({ cadence, mix, risks, benchmark });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="ecc-rex-strategy">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-slate-900 text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-600" /> Rex weekly strategy
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="rex-strategy-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Refresh"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!data ? (loading ? <p className="text-slate-500">Asking Rex…</p> : <p className="text-slate-500">No data yet.</p>) :
        <>
          {data.cadence && (
            <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="rex-strategy-cadence">
              <Zap className={`h-4 w-4 mt-0.5 ${data.cadence.status === 'behind' ? 'text-amber-600' : data.cadence.status === 'ahead' ? 'text-emerald-600' : 'text-slate-400'}`} />
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-700">Cadence</div>
                <p className="text-slate-700 text-[13px]">{data.cadence.recommendation}</p>
              </div>
            </div>
          )}
          {data.mix && data.mix.recommendations && data.mix.recommendations.length > 0 && (
            <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="rex-strategy-mix">
              <Target className="h-4 w-4 mt-0.5 text-indigo-600" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-700">Topic mix</div>
                {data.mix.recommendations.map((r, i) => <p key={i} className="text-slate-700 text-[13px]">{r}</p>)}
              </div>
            </div>
          )}
          {data.risks && data.risks.risks_count > 0 && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-2.5" data-testid="rex-strategy-risks">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-red-600" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-red-800">Content risks ({data.risks.risks_count})</div>
                {(data.risks.risks || []).slice(0, 3).map((r, i) => (
                  <p key={i} className="text-red-800 text-[13px]">{r.title || r.card_id}: {r.risk.replace("_"," ")} — {r.why || ""}</p>
                ))}
              </div>
            </div>
          )}
          {data.benchmark && data.benchmark.campaigns && data.benchmark.campaigns.length > 0 && (
            <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="rex-strategy-benchmark">
              <TrendingUp className="h-4 w-4 mt-0.5 text-slate-600" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-700">Campaign pacing</div>
                {data.benchmark.campaigns.map((c, i) => (
                  <p key={i} className="text-slate-700 text-[13px]">{c.name}: {c.imp_progress_pct ?? 0}% impressions · {c.leads_progress_pct ?? 0}% leads</p>
                ))}
              </div>
            </div>
          )}
          {(!data.cadence?.recommendation && (!data.mix?.recommendations?.length) && data.risks?.risks_count === 0 && !(data.benchmark?.campaigns?.length)) && (
            <p className="text-slate-500 text-xs">All quiet. Publish some posts + log the LinkedIn metrics on each card to unlock richer analysis.</p>
          )}
        </>}
      </CardContent>
    </Card>
  );
}


function CsvImportDialog({ open, onClose, workspaceId, onImported }) {
  const [file, setFile] = useState(null);
  const [platform, setPlatform] = useState("auto");
  const [dryRun, setDryRun] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const fileInputRef = useRef(null);

  // Reset state whenever the dialog is reopened
  useEffect(() => {
    if (open) {
      setFile(null);
      setPlatform("auto");
      setDryRun(true);
      setResult(null);
      setErrMsg(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!file) {
      setErrMsg("Please choose a CSV file first.");
      return;
    }
    if (!workspaceId) {
      setErrMsg("Workspace not loaded yet.");
      return;
    }
    setSubmitting(true);
    setErrMsg(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("platform", platform);
      fd.append("dry_run", String(dryRun));
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/outcomes/import-csv`,
        { method: "POST", credentials: "include", body: fd }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(body?.detail || `Upload failed (${r.status})`);
      }
      setResult(body);
      if (dryRun) {
        toast.success(`Dry run: ${body.matched}/${body.rows_parsed} rows would be imported.`);
      } else {
        if (body.created > 0) {
          toast.success(`Imported ${body.created} outcome rows.`);
        } else {
          toast.warning(`No rows matched a v2 card. ${body.unmatched} unmatched row(s) shown for review.`);
        }
        if (typeof onImported === "function") onImported();
      }
    } catch (e) {
      setErrMsg(e.message || String(e));
      toast.error(e.message || "CSV import failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
            Import LinkedIn analytics CSV
          </DialogTitle>
          <DialogDescription>
            Buffer's API doesn't expose engagement metrics, so export your post analytics from
            LinkedIn (Page admin → Analytics → Posts → Export) and drop the CSV here. Rows are
            matched to v2 cards via the published post URL or post title.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="csv-file" className="text-sm font-medium">CSV file</Label>
            <Input
              id="csv-file"
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="csv-import-file-input"
            />
            {file && (
              <p className="text-xs text-slate-500" data-testid="csv-import-filename">
                Selected: {file.name} · {Math.round(file.size / 1024)} KB
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="csv-platform" className="text-sm font-medium">Platform</Label>
              <select
                id="csv-platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="csv-import-platform-select"
              >
                <option value="auto">Auto-detect</option>
                <option value="linkedin">LinkedIn</option>
                <option value="twitter">Twitter / X</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Mode</Label>
              <label className="flex items-center gap-2 h-9 rounded-md border border-slate-200 bg-white px-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  data-testid="csv-import-dry-run-toggle"
                />
                Dry run (preview only)
              </label>
            </div>
          </div>

          {errMsg && (
            <div
              className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"
              data-testid="csv-import-error"
            >
              {errMsg}
            </div>
          )}

          {result && (
            <div
              className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm space-y-2"
              data-testid="csv-import-result"
            >
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge className="bg-slate-200 text-slate-900 border-slate-300">
                  Parsed {result.rows_parsed}
                </Badge>
                {result.platform && (
                  <Badge className="bg-indigo-100 text-indigo-900 border-indigo-300 capitalize" data-testid="csv-import-detected-platform">
                    Platform: {result.platform}
                    {result.detected_platform && result.detected_platform !== result.platform ? ` (detected: ${result.detected_platform})` : ""}
                  </Badge>
                )}
                <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300">
                  Matched {result.matched}
                </Badge>
                <Badge className="bg-amber-100 text-amber-900 border-amber-300">
                  Unmatched {result.unmatched}
                </Badge>
                {!result.dry_run && (
                  <Badge className="bg-indigo-100 text-indigo-900 border-indigo-300">
                    Saved {result.created}
                  </Badge>
                )}
                {result.dry_run && (
                  <Badge className="bg-sky-100 text-sky-900 border-sky-300">Dry run</Badge>
                )}
              </div>
              {result.columns_detected?.length > 0 && (
                <p className="text-xs text-slate-600">
                  Columns detected: {result.columns_detected.join(", ")}
                </p>
              )}
              {result.preview?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-700 mt-1">Matched preview</p>
                  <ul className="text-xs text-slate-700 space-y-0.5 mt-1">
                    {result.preview.map((row, i) => (
                      <li key={i} className="truncate">
                        · {(row.title || row.post_url || "(unnamed)").slice(0, 70)} →{" "}
                        {row.impressions} impr · {row.reactions + row.comments + row.shares} eng
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.unmatched_preview?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-800 mt-1">Unmatched preview</p>
                  <ul className="text-xs text-amber-800 space-y-0.5 mt-1">
                    {result.unmatched_preview.slice(0, 5).map((row, i) => (
                      <li key={i} className="truncate">
                        · {(row.title || row.post_url || "(no title/url)").slice(0, 90)}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Tip: rows match by post URL (from Buffer publish events) or by post title.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="csv-import-cancel-btn">
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!file || submitting}
            data-testid="csv-import-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…
              </>
            ) : dryRun ? (
              "Preview"
            ) : (
              "Import outcomes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
